import {
	IMAGE_SIGNED_UPLOAD_MAX_BYTES,
	IMAGE_UPLOAD_MAX_BYTES,
} from '@run-diffusion/shared';
import { UploadMediaError } from '@/utils/uploadApi';
import { requiresServerNormalization } from '@/utils/imageUtils';

/**
 * DEPENDENCY LEAF. This module may import `@run-diffusion/shared`,
 * `@/utils/uploadApi` and `@/utils/imageUtils`, and nothing else from
 * `web/src`. It must never import `@/utils/runnitUpload`, which imports it.
 *
 * That is not style. A cycle here would resolve at runtime most of the time and
 * then one day leave `err instanceof UploadImageError` evaluating against an
 * undefined binding, collapsing every classified failure back to UNKNOWN, which
 * is the exact bug IMG-2026-025 existed to fix.
 */

/**
 * Why an upload failed, in terms a caller can turn into a real message.
 *
 * The generic "upload failed" that callers used to surface made field reports
 * undiagnosable: a blocked corporate proxy, an expired session, and a 30MB PNG
 * all looked identical. Each of these maps to a different user action.
 */
export type UploadImageFailureKind =
	| 'NOT_SIGNED_IN'      // no Firebase user, or the ID token could not be minted
	| 'NETWORK_UNREACHABLE' // fetch rejected: offline, DNS/proxy block, or CORS rejection
	| 'TOO_LARGE'          // over the ceiling that applies to this file's path
	| 'REJECTED'           // 4xx: the server understood and refused it
	| 'SERVER_ERROR'       // 5xx, or a 2xx whose body we cannot use
	// The browser could not build a screening proxy for a file it is supposed to
	// be able to decode, so the upload is stopped rather than screened by posting
	// the full source. See buildScreeningProxy.
	| 'SCREENING_UNAVAILABLE'
	| 'UNKNOWN';

export class UploadImageError extends Error {
	readonly kind: UploadImageFailureKind;
	readonly status: number | null;
	/** Verbatim message from the server, when it sent one. Diagnostics only: see isTrustedServerMessage. */
	readonly serverMessage: string;
	/**
	 * True only when serverMessage came from our own function's `{ error }` JSON.
	 * A body from an intercepting proxy or CDN is captured for logs but must
	 * never reach the UI: it is arbitrary HTML, not a sentence for a snackbar.
	 */
	readonly isTrustedServerMessage: boolean;
	/** Correlation id from the function's 500 handler, for Cloud Logging lookups. */
	readonly errorId: string | null;

	constructor (
		kind: UploadImageFailureKind,
		message: string,
		options?: {
			status?: number | null,
			serverMessage?: string,
			isTrustedServerMessage?: boolean,
			errorId?: string | null,
			cause?: unknown,
		},
	) {
		super(message);
		// Subclassing a built-in under an ES5 target loses the prototype chain, which
		// silently breaks `instanceof` and would collapse every classified failure
		// back to "unknown" at the call site. Restore it explicitly.
		Object.setPrototypeOf(this, UploadImageError.prototype);
		this.name = 'UploadImageError';
		this.kind = kind;
		this.status = options?.status ?? null;
		this.serverMessage = options?.serverMessage || '';
		this.isTrustedServerMessage = !!options?.isTrustedServerMessage;
		this.errorId = options?.errorId || null;
		// `cause` is not in the ES2015 Error signature TS targets here; attach it directly.
		if (options?.cause !== undefined) {
			(this as Error & { cause?: unknown }).cause = options.cause;
		}
	}
}

const BYTES_PER_MB: number = 1000 * 1000;

/**
 * Size wording for messages the user reads, e.g. `29.4 MB`.
 *
 * Decimal MB, not `bytes / 1024 / 1024`: the latter is binary MiB under a
 * decimal label, so it disagrees with what the user's OS reports for the same
 * file (macOS Finder calls 29,400,000 bytes "29.4 MB", not "28.0MB").
 *
 * The actual size rounds up and the limit rounds down, so a file only slightly
 * over the cap can never render as "29.4 MB, over the 29.4 MB limit". The
 * sentence stays literally true at every input.
 *
 * pretty-bytes would be the shared choice, but v6 ships ESM only and this module
 * is imported by jest specs that do not transform node_modules.
 */
export const formatUploadSize = (bytes: number): string =>
	`${(Math.ceil((bytes / BYTES_PER_MB) * 10) / 10).toFixed(1)} MB`;

/** Companion to formatUploadSize: rounds down so a stated limit is never overstated. */
export const formatUploadLimit = (bytes: number): string =>
	`${(Math.floor((bytes / BYTES_PER_MB) * 10) / 10).toFixed(1)} MB`;

/**
 * The ceiling that applies to this file, which depends on the path it takes.
 *
 * Quoting the 100MB signed ceiling at a 50MB HEIC would be worse than saying
 * nothing: HEIC goes to the normalizing POST function, which is bounded by the
 * Cloud Functions request body cap, so the user would retry something that
 * cannot work.
 */
export const maxUploadBytesFor = (file: File): number =>
	requiresServerNormalization(file) ? IMAGE_UPLOAD_MAX_BYTES : IMAGE_SIGNED_UPLOAD_MAX_BYTES;

/**
 * Format-aware size gate, for the earliest point a file is known.
 *
 * Without this the server's bare "File exceeds size limit" reaches the caller
 * with no size in it, and uploadMediaInit sends it as a 400 rather than a 413,
 * so nothing downstream can even tell it was a size problem.
 */
export const preflightUploadSize = (file: File): void => {
	const maxBytes: number = maxUploadBytesFor(file);
	if (file.size > maxBytes) {
		throw new UploadImageError(
			'TOO_LARGE',
			`Image is ${formatUploadSize(file.size)}, over the ${formatUploadLimit(maxBytes)} upload limit`,
			{ status: 413 },
		);
	}
};

/** The two size refusals uploadMediaInit and uploadMediaFinalize word for themselves. */
const SIZE_REFUSAL_MESSAGES: string[] = [
	'File exceeds size limit',
	'Uploaded file exceeds size limit',
];

const kindForHttpStatus = (err: UploadMediaError): UploadImageFailureKind => {
	// Init and finalize refuse an oversized file with 400, not 413, so status
	// alone cannot classify it. Their exact wording is the only signal there is.
	if (err.isTrustedServerMessage && SIZE_REFUSAL_MESSAGES.includes(err.serverMessage.trim())) {
		return 'TOO_LARGE';
	}
	const status: number = err.status ?? 0;
	if (status === 413) return 'TOO_LARGE';
	if (status === 401 || status === 403) return 'NOT_SIGNED_IN';
	if (status >= 500) return 'SERVER_ERROR';
	return 'REJECTED';
};

/**
 * Adapt a signed-path failure into the shape the rest of the app classifies on,
 * so annotations and masks keep the diagnosis IMG-2026-025 built for the POST
 * path after IMG-2026-096 reroutes them.
 *
 * Maps on `reason` first and on `status` only inside HTTP. An INVALID_RESPONSE
 * carries no status either, and calling it a network failure would tell the
 * user to check a connection that is working.
 */
export const toUploadImageError = (err: unknown): UploadImageError => {
	if (err instanceof UploadImageError) return err;

	if (err instanceof UploadMediaError) {
		const kind: UploadImageFailureKind = err.reason === 'AUTH'
			? 'NOT_SIGNED_IN'
			: err.reason === 'TRANSPORT'
				? 'NETWORK_UNREACHABLE'
				: err.reason === 'INVALID_RESPONSE'
					? 'SERVER_ERROR'
					: kindForHttpStatus(err);
		return new UploadImageError(kind, err.message, {
			status: err.status,
			serverMessage: err.serverMessage,
			isTrustedServerMessage: err.isTrustedServerMessage,
			cause: err,
		});
	}

	return new UploadImageError('UNKNOWN', String((err as { message?: string } | null)?.message || err || 'Unknown error'), {
		cause: err,
	});
};

/**
 * The snackbar copy. Every message names what went wrong and what to try, so a
 * user can either self-serve or tell support something specific.
 *
 * An unclassified error's own message is never rendered: on this path it can be
 * a proxy's HTML error page or a browser's internal wording, neither of which
 * is a sentence for a snackbar. That is why callers who want to say something
 * specific throw a classified UploadImageError instead.
 */
export const getUploadImageErrorMessage = (err: unknown): string => {
	const error: UploadImageError = toUploadImageError(err);
	switch (error.kind) {
		case 'NOT_SIGNED_IN':
			return 'Could not upload: your session expired. Refresh the page to sign back in, then try again.';
		case 'NETWORK_UNREACHABLE':
			return 'Could not upload: we could not reach the upload service. Check your connection and try again. If you are on a company network, a firewall or VPN may be blocking it.';
		case 'TOO_LARGE':
			// Our own preflight words this with the file's actual size and the
			// limit that applies to its path, so it is fit to render. The server's
			// version is the bare "File exceeds size limit", which carries neither,
			// and quoting it would promise detail that is not there. serverMessage
			// is only set on the server-originated one.
			return error.serverMessage
				? 'Could not upload: the image is over the upload limit. Try a smaller image.'
				: `Could not upload: ${error.message}. Try a smaller image.`;
		case 'REJECTED':
			// error.message may be a proxy's HTML error page. Only render text the
			// upload service itself sent as JSON.
			return error.isTrustedServerMessage && error.serverMessage
				? `Could not upload: the image was rejected (${error.serverMessage}).`
				: 'Could not upload: the image was rejected. Please try again, and contact support if it keeps happening.';
		case 'SERVER_ERROR':
			return error.errorId
				? `Could not upload: the upload service had a problem. Please try again, and contact support with reference ${error.errorId} if it keeps happening.`
				: 'Could not upload: the upload service had a problem. Please try again, and contact support if it keeps happening.';
		case 'SCREENING_UNAVAILABLE':
			return 'Could not upload: this image could not be prepared for review. Try saving it as a JPEG or PNG and uploading that.';
		default:
			return 'Could not upload the image. Please try again, and contact support if it keeps happening.';
	}
};
